import { formatUTCToChicago, getWeekEndDate, isSubmissionLate, nowChicago, utcToChicago } from '../../../utils/datetime/datetime-comprehensive';

// Pure calculation helpers for useDashboard: date parsing, ongoing-course
// filtering, assignment/quiz status derivation, due-date formatting, and the
// weekly-assignment "due this week" grouping. Extracted VERBATIM.

export const parseDateOnly = (dateString) => {
  if (!dateString) return null;

  const datePart = String(dateString).split('T')[0];
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return null;

  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getCourseEndDate = (item) => {
  return parseDateOnly(
    item.course_end_date ||
    item.courseEndDate ||
    item.end_date ||
    item.endDate
  );
};

export const isOngoingCourseItem = (item) => {
  const isActive = item.course_is_active === 1 || item.course_is_active === true;
  if (!isActive) return false;

  const endDate = getCourseEndDate(item);
  if (!endDate) return true;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return endDate >= today;
};

// Splits ongoing-course assignments into weekly assignments due in the
// current calendar week vs. everything else (including non-weekly
// assignments). Called from fetchDashboardData once the active-assignments
// list has been fetched and filtered to ongoing courses.
export const computeWeeklyAssignmentGroups = (activeAssignments) => {
  const weeklyDueThisWeek = [];
  const otherAssignments = [];

  activeAssignments.forEach(assignment => {
    if (assignment.allow_weekly_submissions) {
      const startOfCurrentCalendarWeek = nowChicago().startOf('week');
      const endOfCurrentCalendarWeek = nowChicago().endOf('week');
      const now = nowChicago();
      const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];
      const weeklyOverridesByWeek = assignment?.weekly_overrides_by_week && typeof assignment.weekly_overrides_by_week === 'object'
        ? assignment.weekly_overrides_by_week
        : {};

      const overdueWeeks = [];
      let nextOpenWeek = null;
      let nextOpenWeekDueDate = null;

      // Respect assignment max weekly submissions (fallback to 1)
      const parsedMaxWeeks = Number(assignment.max_weekly_submissions);
      const maxWeeks = Number.isFinite(parsedMaxWeeks) && parsedMaxWeeks > 0
        ? Math.floor(parsedMaxWeeks)
        : 1;

      // Iterate through possible weeks (e.g., up to 52 for a year-long assignment)
      for (let weekNumber = 1; weekNumber <= maxWeeks; weekNumber++) {
        let weekDueDate;
        const weekOverride = weeklyOverridesByWeek[weekNumber] ?? weeklyOverridesByWeek[String(weekNumber)];
        const isExtraCreditWeek = weekOverride?.is_extra_credit === true || weekOverride?.is_extra_credit === 1;

        if (weekNumber === 1 && assignment.due_date) {
          weekDueDate = utcToChicago(assignment.due_date); // Use assignment due_date for week 1
        } else if (weekOverride?.due_date) {
          weekDueDate = utcToChicago(weekOverride.due_date);
        } else if (assignment.start_date) {
          weekDueDate = getWeekEndDate(assignment.start_date, weekNumber, breakPeriods);
        } else {
          continue; // No start date, cannot calculate weekly due dates
        }

        if (!weekDueDate && !isExtraCreditWeek) continue;

        // Check if a submission exists for this specific week
        // Use submitted_week_numbers array if available (from backend), otherwise fallback to checking assignment.week_number
        let isSubmittedForThisWeek = false;
        if (assignment.submitted_week_numbers) {
          // Handle both string (JSON) and array formats
          let submittedWeeks = assignment.submitted_week_numbers;
          if (typeof submittedWeeks === 'string') {
            try {
              submittedWeeks = JSON.parse(submittedWeeks);
            } catch (e) {
              // If parsing fails, treat as empty array
              submittedWeeks = [];
            }
          }
          if (Array.isArray(submittedWeeks)) {
            // Backend provides array of submitted week numbers
            // Handle both number and string formats (JSON_ARRAYAGG might return strings)
            isSubmittedForThisWeek = submittedWeeks.some(w => Number(w) === weekNumber);
          }
        }

        // Fallback: if submitted_week_numbers not available, check assignment.week_number
        if (!isSubmittedForThisWeek && assignment.submitted_at && assignment.week_number) {
          // Only mark as submitted if this is the exact week_number returned
          isSubmittedForThisWeek = assignment.week_number === weekNumber;
        }

        // Skip if already submitted for this week
        if (isSubmittedForThisWeek) continue;

        if (isExtraCreditWeek) {
          if (!nextOpenWeek) {
            nextOpenWeek = weekNumber;
            nextOpenWeekDueDate = null;
          }
          continue;
        }

        // Categorize weeks as overdue or pending
        if (weekDueDate < now) {
          // Overdue: due date is in the past
          overdueWeeks.push({
            week_number: weekNumber,
            display_due_date: weekDueDate
          });
        } else {
          // Pending/Open: due date is in the future - only track the NEXT upcoming one
          if (!nextOpenWeek && weekDueDate > now) {
            nextOpenWeek = weekNumber;
            nextOpenWeekDueDate = weekDueDate;
            // Found the next open week, break since we only want the current open one
            break;
          }
        }

        // If weekDueDate is too far in the future (more than 2 months), break the loop
        if (weekDueDate > now.plus({ weeks: 8 })) {
          break;
        }
      }

      // Helper to create week-specific assignment object
      // Clears submitted_at and status if this specific week hasn't been submitted
      const createWeekAssignment = (weekNum, dueDate) => {
        const weekOverride = weeklyOverridesByWeek[weekNum] ?? weeklyOverridesByWeek[String(weekNum)];
        const isExtraCreditWeek = weekOverride?.is_extra_credit === true || weekOverride?.is_extra_credit === 1;
        // Check if THIS specific week was submitted
        let isThisWeekSubmitted = false;
        if (assignment.submitted_week_numbers) {
          let submittedWeeks = assignment.submitted_week_numbers;
          if (typeof submittedWeeks === 'string') {
            try {
              submittedWeeks = JSON.parse(submittedWeeks);
            } catch (e) {
              submittedWeeks = [];
            }
          }
          if (Array.isArray(submittedWeeks)) {
            isThisWeekSubmitted = submittedWeeks.some(w => Number(w) === weekNum);
          }
        }

        const weekAssignment = {
          ...assignment,
          week_number: weekNum,
          display_due_date: isExtraCreditWeek && !weekOverride?.due_date ? null : dueDate,
          due_date: isExtraCreditWeek && !weekOverride?.due_date ? null : assignment.due_date,
          is_required: weekOverride?.is_required !== undefined ? Boolean(weekOverride.is_required) : true,
          is_extra_credit: isExtraCreditWeek,
        };

        // Clear submitted_at and status if THIS week wasn't submitted
        if (!isThisWeekSubmitted) {
          weekAssignment.submitted_at = null;
          weekAssignment.status = null;
        }

        return weekAssignment;
      };

      // Add overdue weeks to weeklyDueThisWeek (if they fall in current calendar week) or otherAssignments
      overdueWeeks.forEach(week => {
        const weekAssignment = createWeekAssignment(week.week_number, week.display_due_date);
        if (week.display_due_date >= startOfCurrentCalendarWeek && week.display_due_date <= endOfCurrentCalendarWeek) {
          weeklyDueThisWeek.push(weekAssignment);
        } else {
          otherAssignments.push(weekAssignment);
        }
      });

      // Add only the NEXT open week if it has actually started (not just future due date)
      // A week has started if its start date (start_date + (weekNumber-1) weeks) is <= now
      if (nextOpenWeek) {
        // Calculate when this week actually starts
        let weekStartDate = null;
        if (assignment.start_date) {
          if (nextOpenWeek === 1) {
            weekStartDate = utcToChicago(assignment.start_date);
          } else {
            // Week N starts at start_date + (N-1) weeks
            const startDate = utcToChicago(assignment.start_date);
            weekStartDate = startDate.plus({ weeks: nextOpenWeek - 1 });
          }
        }

        // Only show the week if it has actually started
        if ((weekStartDate && weekStartDate.isValid && weekStartDate <= now) || nextOpenWeekDueDate === null) {
          const weekAssignment = createWeekAssignment(nextOpenWeek, nextOpenWeekDueDate);
          if (nextOpenWeekDueDate && nextOpenWeekDueDate >= startOfCurrentCalendarWeek && nextOpenWeekDueDate <= endOfCurrentCalendarWeek) {
            weeklyDueThisWeek.push(weekAssignment);
          } else {
            otherAssignments.push(weekAssignment);
          }
        }
      }

    } else {
      otherAssignments.push(assignment);
    }
  });

  return { weeklyDueThisWeek, otherAssignments };
};

export const getAssignmentStatus = (assignment) => {
  if (assignment.is_extra_credit && assignment.allow_weekly_submissions && assignment.week_number) {
    let submittedWeeks = assignment.submitted_week_numbers || [];
    if (typeof submittedWeeks === 'string') {
      try {
        submittedWeeks = JSON.parse(submittedWeeks);
      } catch (e) {
        submittedWeeks = [];
      }
    }

    return Array.isArray(submittedWeeks) && submittedWeeks.some(w => Number(w) === Number(assignment.week_number))
      ? 'completed'
      : 'pending';
  }

  // If it's a weekly assignment with a specific week_number and display_due_date
    if (assignment.allow_weekly_submissions && assignment.week_number && assignment.start_date) {
    const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];
    const now = nowChicago();
    // Use display_due_date if available (already calculated), otherwise calculate it
    const weekDueDate = assignment.display_due_date || getWeekEndDate(assignment.start_date, assignment.week_number, breakPeriods);

    // Check if THIS specific week has been submitted
    let isThisWeekSubmitted = false;
    if (assignment.submitted_week_numbers) {
      let submittedWeeks = assignment.submitted_week_numbers;
      if (typeof submittedWeeks === 'string') {
        try {
          submittedWeeks = JSON.parse(submittedWeeks);
        } catch (e) {
          submittedWeeks = [];
        }
      }
      if (Array.isArray(submittedWeeks)) {
        isThisWeekSubmitted = submittedWeeks.some(w => Number(w) === assignment.week_number);
      }
    }
    // Fallback to submitted_at if submitted_week_numbers not available
    if (!isThisWeekSubmitted && assignment.submitted_at) {
      isThisWeekSubmitted = true;
    }

    if (isThisWeekSubmitted && assignment.submitted_at) {
      // If submitted, check if it was late for THIS week
      if (isSubmissionLate(assignment.start_date, assignment.week_number, assignment.submitted_at, assignment.due_date, breakPeriods)) {
        return 'late';
      }
      return 'completed';
    } else {
      // If not submitted, check if the current week's due date is overdue
      if (weekDueDate && weekDueDate < now) {
        return 'overdue';
      }
      return 'pending';
    }
  }

  // Existing logic for non-weekly assignments and general assignments (not current week's weekly)
  if (assignment.submitted_at) {
    if (assignment.due_date) {
      const submittedAt = new Date(assignment.submitted_at);
      const dueDate = new Date(assignment.due_date);
      if (!isNaN(dueDate.getTime()) && dueDate.getTime() !== 0 && submittedAt > dueDate) {
        return 'late';
      }
    }
    return 'completed';
  }

  if (!assignment.due_date || assignment.due_date === null || assignment.due_date === undefined) {
    return 'pending';
  }

  const now = nowChicago();
  let dueDate;
  try {
    dueDate = utcToChicago(assignment.due_date);
  } catch (e) {
    return 'pending';
  }

  if (!dueDate || !dueDate.isValid) {
    return 'pending';
  }

  return dueDate < now ? 'overdue' : 'pending';
};

export const getQuizStatus = (quiz) => {
  switch (quiz.status) {
    case 'completed': return 'completed';
    case 'in_progress': return 'in-progress';
    default: {
      // Check if quiz is overdue
      if ((quiz.status === 'not_started' || quiz.status === 'in_progress') &&
          (quiz.assignment_due_at || quiz.quiz_due_at)) {
        const dueDate = new Date(quiz.assignment_due_at || quiz.quiz_due_at);
        const now = new Date();
        if (!isNaN(dueDate.getTime()) && dueDate.getTime() !== 0 && dueDate < now) {
          return 'overdue';
        }
      }
      return 'pending';
    }
  }
};

export const formatDueDate = (dueDate, assignment = null) => {
  if (assignment?.is_extra_credit && !assignment?.display_due_date && !assignment?.due_date) {
    return '';
  }

  if (assignment?.allow_weekly_submissions && assignment?.week_number && assignment?.display_due_date) {
    return formatUTCToChicago(assignment.display_due_date.toUTC().toISO(), 'M/d/yyyy \'at\' h:mm a');
  }

  if (!dueDate || dueDate === null || dueDate === undefined) {
    return 'No Due Date';
  }

  // Check if it's a valid date and not the Unix epoch
  const date = new Date(dueDate);
  if (isNaN(date.getTime()) || date.getTime() === 0) {
    return 'No Due Date';
  }

  return formatUTCToChicago(dueDate, 'M/d/yyyy \'at\' h:mm a');
};
